import React, { useState } from "react";
import { useParams, Link, Navigate } from "react-router-dom";
import {
  AlertTriangle,
  CheckCircle2,
  ArrowRight,
  Phone,
  Plus,
  Minus,
  Shield,
  FileText,
  BookOpen,
} from "lucide-react";
import Header from "./Header";
import Footer from "./Footer";
import Breadcrumbs from "./Breadcrumbs";
import SEO from "./SEO";
import StickyCTA from "./StickyCTA";
import CalculatorCallout from "./CalculatorCallout";
import RelatedClaimHelp from "./RelatedClaimHelp";
import ContentSections from "./ContentSections";
import ReviewedBy from "./ReviewedBy";
import { landingPages } from "../data/landingPages";
import { businessInfo } from "../mock";
import {
  localBusinessSchema,
  serviceSchema,
  faqSchema,
  breadcrumbSchema,
} from "../utils/schemas";

const LANDING_TOPIC = {
  "denied-insurance-claim-florida": "denied",
  "underpaid-insurance-claim-florida": "underpaid",
  "roof-claim-denial-florida": "roof",
};

const LandingPage = () => {
  const { slug } = useParams();
  const page = landingPages[slug];
  const [openFaq, setOpenFaq] = useState(0);

  if (!page) return <Navigate to="/" replace />;

  // Topic-aware contact href so the form pre-fills the message
  const contactHref = `/?topic=${encodeURIComponent(page.slug)}#contact`;

  const schemas = [
    localBusinessSchema(),
    serviceSchema({
      name: page.targetKeyword,
      description: page.metaDescription,
      slug: page.slug,
    }),
    faqSchema(page.faqs),
    breadcrumbSchema([
      { name: "Home", path: "/" },
      { name: page.eyebrow, path: `/${page.slug}` },
    ]),
  ];

  return (
    <div className="bg-black min-h-screen text-white">
      <SEO
        title={page.metaTitle}
        description={page.metaDescription}
        path={`/${page.slug}`}
        keywords={page.keywords}
        image={`https://claimbeast.com${page.image}`}
        schemas={schemas}
      />
      <Header />

      <Breadcrumbs items={[{ name: page.eyebrow, path: `/${page.slug}` }]} />

      {/* Hero */}
      <section className="relative bg-black overflow-hidden" data-testid="landing-hero">
        <div className="absolute inset-0">
          <img
            src={page.image}
            alt={`${page.targetKeyword} help Florida`}
            className="w-full h-full object-cover opacity-25"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-black via-black/90 to-black/30" />
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_20%_50%,rgba(225,29,38,0.18),transparent_55%)]" />
        </div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 py-20 md:py-28">
          <div className="max-w-3xl">
            <div className="flex items-center gap-3 mb-5">
              <span className="h-[2px] w-10 bg-[#E11D26]" />
              <span className="text-[#E11D26] text-xs sm:text-sm font-bold tracking-[0.25em] uppercase">
                {page.eyebrow}
              </span>
            </div>
            <h1
              className="text-white text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-black uppercase leading-[0.92]"
              style={{ fontFamily: "'Anton', 'Oswald', sans-serif" }}
            >
              {page.h1.split(page.h1Highlight)[0]}
              <span className="text-[#E11D26]">{page.h1Highlight}</span>
            </h1>
            <p className="mt-6 text-white/85 text-base sm:text-lg leading-relaxed max-w-2xl">
              {page.intro}
            </p>
            <div className="mt-9 flex flex-col sm:flex-row gap-3">
              <Link
                to={contactHref}
                data-testid="landing-cta-primary"
                className="group inline-flex items-center justify-between gap-6 bg-[#E11D26] hover:bg-[#c11820] text-white font-bold uppercase tracking-wider px-7 py-4 transition-colors"
              >
                <span className="text-sm">Get a Free Claim Review</span>
                <ArrowRight className="h-5 w-5 transition-transform group-hover:translate-x-1" />
              </Link>
              <a
                href={`tel:${businessInfo.phoneRaw}`}
                data-testid="landing-cta-phone"
                className="inline-flex items-center justify-center gap-2 bg-white text-black hover:bg-white/90 font-bold uppercase tracking-wider px-7 py-4 transition-colors"
              >
                <Phone className="h-4 w-4" />
                <span className="text-sm">{businessInfo.phone}</span>
              </a>
            </div>
            <div className="mt-7 flex flex-wrap items-center gap-x-6 gap-y-2 text-white/65 text-xs sm:text-sm">
              <span className="flex items-center gap-2">
                <Shield className="h-4 w-4 text-[#E11D26]" />
                FL License W757540
              </span>
              <span className="flex items-center gap-2">
                <Shield className="h-4 w-4 text-[#E11D26]" />
                Firm License G283331
              </span>
              <span className="flex items-center gap-2">
                <FileText className="h-4 w-4 text-[#E11D26]" />
                No win, no fee
              </span>
            </div>
          </div>
        </div>
      </section>

      {/* Stats strip */}
      {page.showStats && (
        <section className="bg-[#E11D26]" data-testid="landing-stats">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 py-8">
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-6 sm:gap-8 text-center sm:text-left">
              {page.statsBlock.stats.map((s) => (
                <div key={s.label}>
                  <div
                    className="text-white text-4xl sm:text-5xl font-black leading-none"
                    style={{ fontFamily: "'Anton', 'Oswald', sans-serif" }}
                  >
                    {s.value}
                  </div>
                  <div className="mt-2 text-white/90 text-xs sm:text-sm font-bold tracking-wider uppercase">
                    {s.label}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Pain points / Why denied */}
      <section className="py-16 md:py-20 bg-[#0A0A0A]" data-testid="landing-pain">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="max-w-3xl">
            <h2
              className="text-3xl sm:text-4xl md:text-5xl font-black text-white uppercase leading-tight"
              style={{ fontFamily: "'Anton', 'Oswald', sans-serif" }}
            >
              {page.painSectionTitle}
            </h2>
            <p className="mt-4 text-white/70 text-base leading-relaxed">
              {page.painSectionSub}
            </p>
          </div>
          <div className="mt-10 grid md:grid-cols-2 gap-5">
            {page.painPoints.map((p, i) => (
              <article
                key={i}
                className="bg-[#0F0F0F] border border-white/10 hover:border-[#E11D26]/40 p-6 transition-colors"
              >
                <div className="flex items-start gap-3">
                  <AlertTriangle className="h-5 w-5 text-[#E11D26] shrink-0 mt-1" />
                  <div>
                    <h3
                      className="text-white font-black uppercase text-lg tracking-wide"
                      style={{ fontFamily: "'Anton', 'Oswald', sans-serif" }}
                    >
                      {p.title}
                    </h3>
                    <p className="mt-2 text-white/75 text-sm leading-relaxed">
                      {p.text}
                    </p>
                  </div>
                </div>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* Steps / Process */}
      <section className="py-16 md:py-20 bg-black" data-testid="landing-steps">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <h2
            className="text-3xl sm:text-4xl md:text-5xl font-black text-white uppercase leading-tight max-w-3xl"
            style={{ fontFamily: "'Anton', 'Oswald', sans-serif" }}
          >
            {page.stepsSectionTitle}
          </h2>
          <div className="mt-10 grid md:grid-cols-5 gap-px bg-white/10 border border-white/10">
            {page.steps.map((step) => (
              <div key={step.n} className="bg-[#0F0F0F] p-6 sm:p-7">
                <div
                  className="text-[#E11D26] text-4xl font-black leading-none"
                  style={{ fontFamily: "'Anton', 'Oswald', sans-serif" }}
                >
                  {step.n}
                </div>
                <h3
                  className="mt-4 text-white font-black uppercase text-base tracking-wide leading-tight"
                  style={{ fontFamily: "'Anton', 'Oswald', sans-serif" }}
                >
                  {step.title}
                </h3>
                <p className="mt-3 text-white/70 text-sm leading-relaxed">
                  {step.text}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {page.extraSections && <ContentSections sections={page.extraSections} />}

      {/* Calculator callout — topic-aware */}
      <CalculatorCallout
        eyebrow={`${page.eyebrow} · Estimate In 60 Seconds`}
        headline={`See what your ${page.targetKeyword.toLowerCase()} could actually pay.`}
        body={`Most ${page.targetKeyword.toLowerCase()} cases we take on were undervalued by thousands before we got involved. Run your damage through our calculator — it pulls from real Florida adjuster pricing for roof, water, wind, and interior loss and gives you a low–high recovery range you can sanity-check the carrier's number against.`}
        ctaLabel="Run My Estimate"
      />

      {/* CTA Band */}
      <section className="bg-gradient-to-r from-[#1a0709] via-black to-[#1a0709] py-12 border-y border-[#E11D26]/30">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 flex flex-col md:flex-row items-center justify-between gap-6">
          <div className="flex items-start gap-4">
            <CheckCircle2 className="h-8 w-8 text-[#E11D26] shrink-0" />
            <div>
              <h3
                className="text-white text-xl sm:text-2xl md:text-3xl font-black uppercase leading-tight"
                style={{ fontFamily: "'Anton', 'Oswald', sans-serif" }}
              >
                Your free Florida claim review takes 5 minutes.
              </h3>
              <p className="mt-1 text-white/65 text-sm">
                No upfront cost. No obligation. We only get paid if you do.
              </p>
            </div>
          </div>
          <div className="flex flex-col sm:flex-row gap-3 shrink-0">
            <Link
              to={contactHref}
              data-testid="landing-cta-mid"
              className="inline-flex items-center justify-center gap-2 bg-[#E11D26] hover:bg-[#c11820] text-white font-bold uppercase tracking-wider px-6 py-3.5 text-sm transition-colors"
            >
              Free Claim Review
              <ArrowRight className="h-4 w-4" />
            </Link>
            <a
              href={`tel:${businessInfo.phoneRaw}`}
              className="inline-flex items-center justify-center gap-2 bg-transparent border border-white text-white hover:bg-white hover:text-black font-bold uppercase tracking-wider px-6 py-3.5 text-sm transition-colors"
            >
              <Phone className="h-4 w-4" />
              {businessInfo.phone}
            </a>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-16 md:py-20 bg-[#0A0A0A]" data-testid="landing-faq">
        <div className="max-w-4xl mx-auto px-4 sm:px-6">
          <div className="flex items-center gap-3 mb-4">
            <span className="h-[2px] w-10 bg-[#E11D26]" />
            <span className="text-[#E11D26] text-xs sm:text-sm font-bold tracking-[0.25em] uppercase">
              Florida Claim FAQ
            </span>
          </div>
          <h2
            className="text-3xl sm:text-4xl md:text-5xl font-black text-white uppercase leading-tight"
            style={{ fontFamily: "'Anton', 'Oswald', sans-serif" }}
          >
            Common <span className="text-[#E11D26]">Questions</span>
          </h2>
          <div className="mt-9 divide-y divide-white/10 border-y border-white/10">
            {page.faqs.map((faq, i) => {
              const open = openFaq === i;
              return (
                <div key={i} className="py-1">
                  <button
                    onClick={() => setOpenFaq(open ? -1 : i)}
                    data-testid={`landing-faq-${i}`}
                    className="w-full text-left flex items-start justify-between gap-6 py-5 group"
                  >
                    <span className="text-white font-bold text-base sm:text-lg group-hover:text-[#E11D26] transition-colors">
                      {faq.question}
                    </span>
                    <span className="shrink-0 text-[#E11D26]">
                      {open ? (
                        <Minus className="h-5 w-5" />
                      ) : (
                        <Plus className="h-5 w-5" />
                      )}
                    </span>
                  </button>
                  {open && (
                    <p className="pb-6 text-white/75 leading-relaxed text-[15px]">
                      {faq.answer}
                    </p>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Related Guides — prominent supporting-content block */}
      {page.guides && (
        <section
          className="py-16 bg-[#0A0A0A] border-y border-white/10"
          data-testid="landing-guides"
        >
          <div className="max-w-7xl mx-auto px-4 sm:px-6">
            <div className="flex items-center gap-3 mb-4">
              <BookOpen className="h-5 w-5 text-[#E11D26]" />
              <span className="text-[#E11D26] text-xs sm:text-sm font-bold tracking-[0.25em] uppercase">
                Related Guides
              </span>
            </div>
            <h2
              className="text-2xl sm:text-3xl md:text-4xl font-black text-white uppercase leading-tight max-w-3xl"
              style={{ fontFamily: "'Anton', 'Oswald', sans-serif" }}
            >
              Read Before You <span className="text-[#E11D26]">Deal With The Carrier</span>
            </h2>
            <div className="mt-8 grid md:grid-cols-3 gap-px bg-white/10 border border-white/10">
              {page.guides.map((g) => (
                <Link
                  key={g.href}
                  to={g.href}
                  data-testid={`landing-guide-${g.href.split("/").pop()}`}
                  className="group bg-[#0F0F0F] hover:bg-[#1a0709] p-7 transition-colors flex flex-col"
                >
                  <span className="text-[#E11D26] text-xs font-bold tracking-[0.25em] uppercase">
                    {g.tag}
                  </span>
                  <h3
                    className="mt-3 text-white font-black uppercase text-lg tracking-wide leading-snug group-hover:text-[#E11D26] transition-colors"
                    style={{ fontFamily: "'Anton', 'Oswald', sans-serif" }}
                  >
                    {g.title}
                  </h3>
                  <span className="mt-4 inline-flex items-center gap-2 text-white/70 text-sm font-bold tracking-wider uppercase">
                    Read Guide
                    <ArrowRight className="h-4 w-4 text-[#E11D26] transition-transform group-hover:translate-x-1" />
                  </span>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Related cross-links */}
      <section className="py-14 bg-black" data-testid="landing-related">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <h2
            className="text-2xl sm:text-3xl font-black text-white uppercase"
            style={{ fontFamily: "'Anton', 'Oswald', sans-serif" }}
          >
            {page.relatedTitle}
          </h2>
          <div className="mt-6 grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {page.related.map((r) => (
              <Link
                key={r.href}
                to={r.href}
                className="group flex items-center justify-between gap-4 bg-[#0F0F0F] border border-white/10 hover:border-[#E11D26] hover:bg-[#1a0709] p-5 transition-colors"
              >
                <span className="text-white font-bold text-sm uppercase tracking-wide">
                  {r.text}
                </span>
                <ArrowRight className="h-4 w-4 text-[#E11D26] transition-transform group-hover:translate-x-1" />
              </Link>
            ))}
          </div>
        </div>
      </section>

      <ReviewedBy />

      <RelatedClaimHelp
        topic={LANDING_TOPIC[page.slug] || "generic"}
        currentPath={`/${page.slug}`}
      />


      {/* Final CTA */}
      <section className="py-14 bg-[#E11D26]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 flex flex-col md:flex-row items-center justify-between gap-6">
          <h3
            className="text-white text-2xl sm:text-3xl md:text-4xl font-black uppercase leading-tight"
            style={{ fontFamily: "'Anton', 'Oswald', sans-serif" }}
          >
            Stop leaving money on the table.
          </h3>
          <Link
            to={contactHref}
            data-testid="landing-cta-final"
            className="group inline-flex items-center gap-3 bg-black hover:bg-[#0A0A0A] text-white font-bold uppercase tracking-wider px-7 py-4 transition-colors"
          >
            <span className="text-sm">Start My Free Review</span>
            <ArrowRight className="h-5 w-5 transition-transform group-hover:translate-x-1" />
          </Link>
        </div>
      </section>

      <StickyCTA topicSlug={page.slug} label="Free Claim Review" />
      {/* Spacer so sticky CTA never covers footer content on mobile */}
      <div aria-hidden="true" className="h-20 sm:h-16" />
      <Footer />
    </div>
  );
};

export default LandingPage;
